import { default as React } from 'react';
import { useState, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from '@mui/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const useStyles = makeStyles(theme => ({
  available: {
    color: theme.palette.success.main,
  },
  unavailable: {
    color: theme.palette.error.main,
  },
  checking: {
    color: theme.palette.grey[500],
  },
}));

export const AppNameField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [checking, setChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [normalizedName, setNormalizedName] = useState<string>('');

  // Function to normalize app name: convert to lowercase and replace special chars with hyphens
  const normalizeAppName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric chars with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .replace(/-+/g, '-'); // Replace multiple consecutive hyphens with single hyphen
  };

  useEffect(() => {
    const checkAvailability = async () => {
      if (!formData || formData.trim() === '') {
        setIsAvailable(null);
        setErrorMessage('');
        setNormalizedName('');
        return;
      }

      // Normalize the app name
      const normalized = normalizeAppName(formData);
      setNormalizedName(normalized);

      // Validate length (3-50 characters) on the original input
      if (formData.length < 3) {
        setIsAvailable(false);
        setErrorMessage('App name must be at least 3 characters long');
        return;
      }

      if (formData.length > 50) {
        setIsAvailable(false);
        setErrorMessage('App name must not exceed 50 characters');
        return;
      }

      // Validate minimum length on normalized name
      if (normalized.length < 3) {
        setIsAvailable(false);
        setErrorMessage(
          'App name must be at least 3 characters long after normalization',
        );
        return;
      }

      setChecking(true);
      setErrorMessage('');

      try {
        // Query catalog for entities with matching normalized name or variations
        const namesToCheck = [
          normalized,
          `${normalized}-wst`,
          `${normalized}-minc`,
        ];

        // Check all name variations
        const results = await Promise.all(
          namesToCheck.map(name =>
            catalogApi.getEntities({
              filter: {
                kind: 'Component',
                'metadata.name': name,
              },
            }),
          ),
        );

        // Check if any variant exists
        const existingApps = results.flatMap((result, index) =>
          result.items.map(item => ({
            name: namesToCheck[index],
            entity: item,
          })),
        );

        if (existingApps.length > 0) {
          // App name is taken
          setIsAvailable(false);
          const errorMsg = `App name conflict: ${formData} already registered`;
          setErrorMessage(errorMsg);
        } else {
          // App name is available
          setIsAvailable(true);
          setErrorMessage('');
        }
      } catch (error) {
        // Error querying catalog - allow user to continue
        setIsAvailable(null);
        setErrorMessage(
          'Unable to verify availability. You can continue, and it will be validated on submission.',
        );
      } finally {
        setChecking(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(() => {
      checkAvailability();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData, catalogApi]);

  const getEndAdornment = () => {
    if (checking) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} className={classes.checking} />
        </InputAdornment>
      );
    }
    if (isAvailable === true) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon className={classes.available} />
        </InputAdornment>
      );
    }
    if (isAvailable === false) {
      return (
        <InputAdornment position="end">
          <ErrorIcon className={classes.unavailable} />
        </InputAdornment>
      );
    }
    return null;
  };

  const getHelperText = () => {
    if (checking) {
      return <span className={classes.checking}>Checking availability...</span>;
    }
    if (isAvailable === true && normalizedName) {
      return (
        <span className={classes.available}>
          ✓ App name "{normalizedName}" is available
        </span>
      );
    }
    if (errorMessage) {
      return <span className={classes.unavailable}>{errorMessage}</span>;
    }
    if (rawErrors && rawErrors.length > 0) {
      return (
        <span className={classes.unavailable}>{rawErrors.join(', ')}</span>
      );
    }
    if (normalizedName && formData) {
      return `Will be saved as: ${normalizedName}`;
    }
    return (
      schema.description ||
      uiSchema?.['ui:description'] ||
      'Enter your application name'
    );
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 || isAvailable === false}
      fullWidth
    >
      <TextField
        id={idSchema?.$id}
        label={schema.title || 'Application Name'}
        value={formData || ''}
        onChange={e => onChange(e.target?.value)}
        required={required}
        error={rawErrors?.length > 0 || isAvailable === false}
        InputProps={{
          endAdornment: getEndAdornment(),
        }}
        placeholder="Application Name"
      />
      <FormHelperText>{getHelperText()}</FormHelperText>
    </FormControl>
  );
};
